import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getTenants, type TenantOption } from '@/services/tenantsApi';
import {
  getFallbackTaxYearConfig,
  getTaxYearConfig,
} from '@/services/taxYearApi';

export interface AdminScopeContextValue {
  /** Currently selected tenant ID, or null to use request tenant (from host) */
  selectedTenantId: string | null;
  setSelectedTenantId: (id: string | null) => void;
  /** Currently selected tax year */
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  /** Tenants for dropdown (from GET /api/tenants) */
  availableTenants: TenantOption[];
  /** Supported tax years from the server (newest first) */
  availableYears: number[];
  /** True while tenants and tax year config are loading */
  isLoading: boolean;
}

const AdminScopeContext = createContext<AdminScopeContextValue | null>(null);

const ADMIN_TENANT_STORAGE_KEY = 'admin-selected-tenant-id';
const ADMIN_TAX_YEAR_STORAGE_KEY = 'admin-selected-tax-year';

function getStoredAdminTaxYear(): number | null {
  try {
    const raw = localStorage.getItem(ADMIN_TAX_YEAR_STORAGE_KEY);
    if (raw == null) return null;
    const year = parseInt(raw, 10);
    if (Number.isNaN(year) || !Number.isInteger(year)) return null;
    return year;
  } catch {
    return null;
  }
}

function setStoredAdminTaxYear(year: number): void {
  try {
    localStorage.setItem(ADMIN_TAX_YEAR_STORAGE_KEY, String(year));
  } catch {
    // ignore
  }
}

/** Newest years first for the dropdown */
function sortYearsDescending(years: number[]): number[] {
  return [...years].sort((a, b) => b - a);
}

interface AdminScopeProviderProps {
  children: ReactNode;
}

/**
 * Provides tenant and tax year selection for the admin layout.
 * Use only when rendering admin routes (AdminLayout).
 */
export function AdminScopeProvider({ children }: AdminScopeProviderProps) {
  const [selectedTenantId, setSelectedTenantIdState] = useState<string | null>(
    () => localStorage.getItem(ADMIN_TENANT_STORAGE_KEY)
  );
  const [selectedYear, setSelectedYearState] = useState<number>(
    () => getStoredAdminTaxYear() ?? new Date().getFullYear() - 1
  );
  const [availableTenants, setAvailableTenants] = useState<TenantOption[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    const applyTaxYearConfig = (
      supportedYears: number[],
      defaultTaxYear: number
    ) => {
      const years = sortYearsDescending(supportedYears);
      setAvailableYears(years);
      const storedYear = getStoredAdminTaxYear();
      const useStoredYear =
        storedYear !== null && years.includes(storedYear);
      const yearToUse = useStoredYear ? storedYear : defaultTaxYear;
      setSelectedYearState(yearToUse);
      if (!useStoredYear) {
        setStoredAdminTaxYear(yearToUse);
      }
    };

    (async () => {
      let tenants: TenantOption[] = [];
      try {
        tenants = await getTenants();
      } catch {
        // leave tenants empty
      }

      let config;
      try {
        config = await getTaxYearConfig();
      } catch {
        config = getFallbackTaxYearConfig();
      }

      if (cancelled) return;

      setAvailableTenants(tenants);
      applyTaxYearConfig(config.supportedYears, config.defaultTaxYear);

      const stored = localStorage.getItem(ADMIN_TENANT_STORAGE_KEY);
      const firstValue = tenants[0]?.value ?? null;
      const validId =
        tenants.length > 0 &&
        (stored && tenants.some((t) => t.value === stored) ? stored : firstValue);
      if (validId) {
        localStorage.setItem(ADMIN_TENANT_STORAGE_KEY, validId);
        setSelectedTenantIdState(validId);
      } else {
        localStorage.removeItem(ADMIN_TENANT_STORAGE_KEY);
        setSelectedTenantIdState(null);
      }
    })().finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const setSelectedTenantId = useCallback((id: string | null) => {
    setSelectedTenantIdState(id);
    if (id) {
      localStorage.setItem(ADMIN_TENANT_STORAGE_KEY, id);
    } else {
      localStorage.removeItem(ADMIN_TENANT_STORAGE_KEY);
    }
  }, []);

  const setSelectedYear = useCallback(
    (year: number) => {
      if (!Number.isInteger(year) || !availableYears.includes(year)) return;
      setSelectedYearState(year);
      setStoredAdminTaxYear(year);
    },
    [availableYears]
  );

  const value = useMemo<AdminScopeContextValue>(
    () => ({
      selectedTenantId,
      setSelectedTenantId,
      selectedYear,
      setSelectedYear,
      availableTenants,
      availableYears,
      isLoading,
    }),
    [
      selectedTenantId,
      setSelectedTenantId,
      selectedYear,
      setSelectedYear,
      availableTenants,
      availableYears,
      isLoading,
    ]
  );

  return (
    <AdminScopeContext.Provider value={value}>
      {children}
    </AdminScopeContext.Provider>
  );
}

export function useAdminScope(): AdminScopeContextValue {
  const ctx = useContext(AdminScopeContext);
  if (!ctx) {
    throw new Error('useAdminScope must be used within AdminScopeProvider (admin layout only).');
  }
  return ctx;
}

export function useAdminScopeOptional(): AdminScopeContextValue | null {
  return useContext(AdminScopeContext);
}
